/**
 * Modal: Run-Start aus den Master-Findings (DB-only).
 * - Run-Name (default: "Sammel-Run YYYY-MM-DD")
 * - Tester-Auswahl (Multi-Checkbox)
 * - Findings-Auswahl: gefilterte Liste der Master-Findings (Status / Stichwort)
 * - Start-Button postet finding_ids an /test-cockpit/runs/start
 */
import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Upload, Loader2, AlertTriangle, Search } from 'lucide-react';
import { testCockpitApi, type Tester, type VaultParseWarning, type MasterFinding } from '../../../lib/testCockpitApi';

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

export default function RunStartModal({ onClose, onCreated }: Props) {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState(`Sammel-Run ${today}`);
  const [testers, setTesters] = useState<Tester[]>([]);
  const [selectedTesters, setSelectedTesters] = useState<Set<string>>(new Set());
  const [findings, setFindings] = useState<MasterFinding[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loadingTesters, setLoadingTesters] = useState(true);
  const [loadingFindings, setLoadingFindings] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<VaultParseWarning[] | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('not-obsolete');
  const [search, setSearch] = useState('');

  useEffect(() => {
    testCockpitApi.listTesters()
      .then(ts => {
        setTesters(ts);
        setSelectedTesters(new Set(ts.map(t => t.id)));
      })
      .catch((e: Error) => setError(`Tester-Liste laden fehlgeschlagen: ${e.message}`))
      .finally(() => setLoadingTesters(false));
    testCockpitApi.listMasterFindings()
      .then(fs => {
        setFindings(fs);
        // Default: alle nicht-obsoleten + nicht-final vorausgewaehlt (das sind die zu testenden)
        setSelected(new Set(fs.filter(f => f.status !== 'obsolete' && f.status !== 'final').map(f => f.id)));
      })
      .catch((e: Error) => setError(`Findings laden fehlgeschlagen: ${e.message}`))
      .finally(() => setLoadingFindings(false));
  }, []);

  const filtered = findings.filter(f => {
    if (filterStatus === 'not-obsolete' && f.status === 'obsolete') return false;
    if (filterStatus !== 'all' && filterStatus !== 'not-obsolete' && f.status !== filterStatus) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!f.title.toLowerCase().includes(s) && !f.source_label.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  function toggleTester(id: string) {
    setSelectedTesters(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function toggleFinding(id: string) {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function selectAllFiltered() {
    setSelected(prev => {
      const n = new Set(prev);
      for (const f of filtered) n.add(f.id);
      return n;
    });
  }
  function deselectAllFiltered() {
    setSelected(prev => {
      const n = new Set(prev);
      for (const f of filtered) n.delete(f.id);
      return n;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setWarnings(null);
    if (selected.size === 0) { setError('Mindestens ein Finding auswählen'); return; }
    if (!name.trim()) { setError('Run-Name eingeben'); return; }
    setBusy(true);
    try {
      const result = await testCockpitApi.startRun({
        name: name.trim(),
        testerIds: Array.from(selectedTesters),
        findingIds: Array.from(selected),
      });
      if (result.warnings.length > 0) setWarnings(result.warnings);
      onCreated();
      navigate(`/test-cockpit/${result.id}`);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const STATUS_BADGE: Record<string, string> = {
    'offen':           'bg-rose-500/15 text-rose-700 dark:text-rose-300',
    'in Verfeinerung': 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    'final':           'bg-brand-500/15 text-brand-700 dark:text-brand-300',
    'obsolete':        'bg-slate-500/15 text-slate-500',
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="bg-card border border-default rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <div className="px-5 py-4 border-b border-default flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Upload size={18} /> Neuen Test-Run anlegen
          </h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Run-Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-background border border-default text-sm"
              placeholder="z.B. Sammel-Run 2026-05-05"
            />
          </div>

          {/* Findings */}
          <div>
            <label className="block text-sm font-medium mb-1">Findings (aus Master-Bestand)</label>
            {loadingFindings ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> laden...</div>
            ) : findings.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">Keine Master-Findings — im Tab „Findings (Master)" anlegen oder Vault importieren.</div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <div className="relative flex-1 min-w-[180px]">
                    <Search size={12} className="absolute left-2 top-2 text-muted-foreground" />
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Suche..."
                      className="pl-7 pr-2 py-1.5 rounded bg-background border border-default text-sm w-full"
                    />
                  </div>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-2 py-1.5 rounded bg-background border border-default text-sm">
                    <option value="not-obsolete">alle ausser obsolete</option>
                    <option value="all">alle</option>
                    <option value="offen">nur offen</option>
                    <option value="in Verfeinerung">nur in Verfeinerung</option>
                    <option value="final">nur final</option>
                  </select>
                  <button type="button" onClick={selectAllFiltered} className="text-xs px-2 py-1.5 rounded hover:bg-primary/5 text-muted-foreground hover:text-foreground">alle</button>
                  <button type="button" onClick={deselectAllFiltered} className="text-xs px-2 py-1.5 rounded hover:bg-primary/5 text-muted-foreground hover:text-foreground">keine</button>
                </div>
                <div className="border border-default rounded p-1 max-h-64 overflow-y-auto">
                  <div className="text-xs text-muted-foreground border-b border-default pb-1 mb-1 px-2">{selected.size} von {filtered.length} ausgewählt</div>
                  {filtered.map(f => (
                    <label key={f.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-primary/5 px-2 py-1 rounded">
                      <input
                        type="checkbox"
                        checked={selected.has(f.id)}
                        onChange={() => toggleFinding(f.id)}
                      />
                      <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${STATUS_BADGE[f.status]}`}>{f.status}</span>
                      <span className="flex-1 truncate">{f.title}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{f.source_label} · #{f.idx + 1}</span>
                    </label>
                  ))}
                  {filtered.length === 0 && <div className="text-sm text-muted-foreground italic px-2 py-2">Keine Treffer.</div>}
                </div>
              </>
            )}
          </div>

          {/* Tester */}
          <div>
            <label className="block text-sm font-medium mb-1">Tester</label>
            {loadingTesters ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> laden...</div>
            ) : testers.length === 0 ? (
              <div className="text-sm text-muted-foreground">Keine Tester mit Permission <code>tests.run</code> gefunden.</div>
            ) : (
              <div className="space-y-1">
                {testers.map(t => (
                  <label key={t.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedTesters.has(t.id)}
                      onChange={() => toggleTester(t.id)}
                    />
                    <span>{t.name || t.email}</span>
                    <span className="text-xs text-muted-foreground">({t.email})</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-rose-500 bg-rose-500/10 border border-rose-500/30 rounded p-3">
              {error}
            </div>
          )}

          {warnings && warnings.length > 0 && (
            <div className="text-sm bg-amber-500/10 border border-amber-500/30 rounded p-3">
              <div className="flex items-center gap-2 font-medium text-amber-700 dark:text-amber-300 mb-1">
                <AlertTriangle size={14} /> {warnings.length} Funde ohne Test-Pfad
              </div>
              <ul className="text-xs space-y-0.5 max-h-40 overflow-y-auto">
                {warnings.map((w, i) => (
                  <li key={i}>· {w.source_file} #{w.idx + 1}: {w.title} — <span className="opacity-70">{w.reason}</span></li>
                ))}
              </ul>
              <div className="text-xs mt-2 opacity-80">Run angelegt — Test-Pfade im Master-Editor nachpflegen.</div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-default flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 rounded-lg text-sm hover:bg-primary/5">
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy || selected.size === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            title="Legt den Run an, spielt das Test-Bundle in die Probst-DB und initialisiert die Tester-Personas."
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Run starten
          </button>
        </div>
      </form>
    </div>
  );
}
